import { useEffect } from 'react';
import { convertOrgType } from 'modules/account/utils';
import { findByKey } from 'modules/account/detail/helpers/utils';
import type { UserDetail } from 'modules/account/services/useGetAccountInfo';
import type { Unit } from 'modules/account/types';

interface UseAccountInfoSyncParams {
  accountData: UserDetail | null | undefined;
  govAgencies:
    | Array<{
        operationCode: string;
        name: string;
        district?: string | null;
        city?: string | null;
        ward?: string | null;
      }>
    | undefined;
  allClients:
    | Array<{
        operationCode: string;
        name: string;
        district?: string | null;
        city?: string | null;
        wardName?: string;
      }>
    | undefined;
  hvsInfo: { company?: Unit } | undefined;
  setUserInfo: React.Dispatch<React.SetStateAction<UserDetail>>;
  setUnitAddress: React.Dispatch<React.SetStateAction<Unit | undefined>>;
}

export function useAccountInfoSync(params: UseAccountInfoSyncParams): void {
  const { accountData, govAgencies, allClients, hvsInfo, setUserInfo, setUnitAddress } = params;

  useEffect(() => {
    if (!accountData?.userAuthen) return;

    const userAuthen = { ...accountData.userAuthen };
    userAuthen.userActualManagingOrgType = convertOrgType(userAuthen.userActualManagingOrgType);

    const clients: Unit[] = (allClients || []).map((item) => ({
      id: item.operationCode,
      operationCode: item.operationCode,
      name: item.name,
      district: item.district || '',
      city: item.city || '',
      ward: item.wardName || '',
    }));

    const govs: Unit[] = (govAgencies || []).map((item) => ({
      id: item.operationCode,
      operationCode: item.operationCode,
      name: item.name,
      district: item.district || '',
      city: item.city || '',
      ward: item.ward || '',
    }));

    const unitId = userAuthen.userActualManagingOrgId;

    switch (userAuthen.userActualManagingOrgType) {
      case 'KHDN':
        setUnitAddress((old) => findByKey(clients, unitId, 'id') || old);
        break;
      case 'CQQL':
        setUnitAddress((old) => findByKey(govs, unitId, 'id') || old);
        break;
      default:
        setUnitAddress(() => (hvsInfo?.company ? hvsInfo.company : undefined));
    }

    // normalize dates coming from API: some endpoints return plain
    // "YYYY-MM-DDT00:00:00" without a timezone. when the picker is still
    // configured to convert to UTC this leads to an off-by-one error for
    // users in positive-offset zones. appending a trailing "Z" makes the
    // value explicit UTC which in turn renders correctly after the change
    // above, and is generally a safe no-op when the string already contains
    // a timezone.
    const ensureUtc = (v?: string | null) => {
      if (!v) return v;
      // if there's already a Z or +/- offset we assume it's fine
      return /Z|[+-]\d{2}:?\d{2}/.test(v) ? v : `${v}Z`;
    };

    setUserInfo({
      ...accountData,
      userAuthen: {
        ...accountData.userAuthen,
        expiredAt: ensureUtc(accountData.userAuthen.expiredAt),
      },
      email: `${userAuthen.email || userAuthen.unverifiedEmail}`,
      noExpired: Boolean(!userAuthen.expiredAt),
      status: Boolean(!userAuthen.inactive),
      unitId,
    });
  }, [accountData, govAgencies, allClients, hvsInfo, setUserInfo, setUnitAddress]);
}
